import * as React from "react";
import ArrowUploadIcon from "@src/lib/icons/18px/arrow-upload";
import CircleExclamationFillIcon from "@src/lib/icons/18px/circle-exclamation-fill";
import Spinner from "@src/lib/ui/Spinner";
import { cn } from "@src/lib/utils/cn";

export type FileDropZoneState = "idle" | "dragging" | "uploading" | "error";

export type FileDropZoneContextValue = {
  isDragActive: boolean;
  // The dragged file will be refused (type or count).
  isDragReject: boolean;
  isUploading: boolean;
  disabled: boolean;
  error: string | null;
};

// Set by FileDropZone so a surface inside it follows the drag/upload state
// without the caller wiring it through.
export const FileDropZoneContext =
  React.createContext<FileDropZoneContextValue | null>(null);

export function useFileDropZoneState(): FileDropZoneContextValue | null {
  return React.useContext(FileDropZoneContext);
}

type Props = {
  // Overrides the state FileDropZone provides; use it outside a drop zone.
  state?: FileDropZoneState;
  title?: React.ReactNode;
  description?: React.ReactNode;
  // Pass null to hide the icon.
  icon?: React.ReactNode;
  // A button row under the text, e.g. "Add media".
  action?: React.ReactNode;
  // Caller-controlled error (e.g. form validation): holds the error state
  // until the caller clears it. Defaults to the drop zone's last error.
  error?: React.ReactNode;
  uploadingLabel?: React.ReactNode;
  className?: string;
};

function resolveState(
  state: FileDropZoneState | undefined,
  context: FileDropZoneContextValue | null,
  error: React.ReactNode,
): FileDropZoneState {
  if (state) return state;
  if (context?.isUploading) return "uploading";
  if (context?.isDragActive) {
    return context.isDragReject ? "error" : "dragging";
  }
  if (error || context?.error) return "error";
  return "idle";
}

const STATE_CLASSNAMES: Record<FileDropZoneState, string> = {
  idle: "border-border-tertiary bg-bg-secondary",
  dragging: "border-brand-border bg-brand-bg",
  uploading: "border-border-tertiary bg-bg-secondary",
  error: "border-status-error-border bg-status-error-bg",
};

/**
 * The dashed drop target drawn inside a FileDropZone (or on its own with
 * `state`). Inside a FileDropZone it follows drag-over, upload and error
 * state automatically.
 */
const FileDropZoneSurface: React.FC<Props> = ({
  state: stateProp,
  title = "Drop a file here or click to upload",
  description,
  icon,
  action,
  error: errorProp,
  uploadingLabel = "Uploading…",
  className,
}) => {
  const context = useFileDropZoneState();
  const state = resolveState(stateProp, context, errorProp);
  const errorMessage = errorProp ?? context?.error;
  const disabled = context?.disabled ?? false;
  // Announce only live errors, not ones rendered on mount.
  const announceError = errorProp == null && Boolean(context?.error);

  return (
    <div
      data-state={state}
      aria-busy={state === "uploading" || undefined}
      aria-disabled={disabled || undefined}
      className={cn(
        "flex w-full flex-col items-center justify-center gap-3 rounded-xl border border-dashed px-6 py-8 text-center transition-colors",
        STATE_CLASSNAMES[state],
        disabled && "opacity-50",
        className,
      )}
    >
      {state === "uploading" ? (
        <>
          <Spinner size={24} className="text-icon-secondary" />
          <p className="text-s-v4 text-text-secondary">{uploadingLabel}</p>
        </>
      ) : (
        <>
          {icon !== null && (
            <div
              className={cn(
                "flex size-10 items-center justify-center rounded-full bg-bg-primary shadow-default",
                state === "dragging" && "text-brand-text",
                state === "error" && "text-status-error-text",
                state === "idle" && "text-icon-secondary",
              )}
            >
              {icon ??
                (state === "error" ? (
                  <CircleExclamationFillIcon className="size-5" />
                ) : (
                  <ArrowUploadIcon className="size-5" />
                ))}
            </div>
          )}
          <div className="flex flex-col items-center gap-1">
            <p className="text-s-v4 font-medium-v4 text-text-primary">
              {title}
            </p>
            {state === "error" && errorMessage ? (
              <p
                role={announceError ? "alert" : undefined}
                className="text-xs-v4 text-status-error-text"
              >
                {errorMessage}
              </p>
            ) : (
              description && (
                <p className="text-xs-v4 text-text-tertiary">{description}</p>
              )
            )}
          </div>
          {action && (
            <div className="flex flex-wrap items-center justify-center gap-2">
              {action}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FileDropZoneSurface;
